import api from '../api/axios';

/**
 * Client for the System Admin insights endpoints — platform telemetry about
 * how much each organisation uses the product.
 *
 * NOTHING here is tenant data. The backing database functions (migration 050)
 * return counts, sums and timestamps only, so no child, staff member, post or
 * image can be reached through these calls. Keep it that way: if a field ever
 * needs to identify a person, it belongs on a different service.
 */

/** The periods the API accepts. Weeks are Monday–Sunday, UK local. */
export type InsightPeriod = 'this_week' | 'last_week' | 'this_month' | 'last_month';

export const INSIGHT_PERIOD_OPTIONS: { id: InsightPeriod; label: string }[] = [
  { id: 'this_week', label: 'This week' },
  { id: 'last_week', label: 'Last week' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
];

/** The window a figure covers, echoed back so the UI can date what it shows. */
export interface PeriodInfo {
  id: InsightPeriod;
  /** ISO instants. The end is exclusive. */
  start: string | null;
  end: string | null;
  unit: 'week' | 'month' | string | null;
  /** How many complete periods the averages are taken over (12 or 6). */
  averagePeriods: number;
}

/** One organisation's row on the Overview table. */
export interface OrgOverviewRow {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
  tierCode: string | null;
  status: string | null;
  trialEndsAt: string | null;
  children: {
    onRoll: number;
    archived: number;
    leavers: number;
    /** On-roll children posted about in the last 28 days — rolling, not period. */
    covered: number;
  };
  staffTotal: number;
  posts: number;
  postsAverage: number;
  photos: number;
  photosAverage: number;
  published: number;
  reports: number;
  aiCost: number;
  aiCostAverage: number;
  unpublishedBacklog: number;
  /** Age of the oldest unpublished post, in days. null when there is none. */
  backlogOldestDays: number | null;
  /** The last thing anyone MADE. Reading isn't recorded, so this is a floor. */
  lastActivityAt: string | null;
}

export interface PlatformInsights {
  period: PeriodInfo;
  organisations: OrgOverviewRow[];
}

/** Everything behind one organisation's Activity tab. */
export interface OrgInsights {
  period: PeriodInfo;
  children: {
    onRoll: number;
    archived: number;
    leavers: number;
    covered14d: number;
    covered28d: number;
    pendingDeletion: number;
    overdueDeletion: number;
  };
  /**
   * Role counts overlap: somebody holding Admin and Editor is counted in both,
   * so they will not add up to `total`. That is the truth about role grants,
   * not a rounding error — the UI says so rather than hiding it.
   */
  staff: {
    total: number;
    admin: number;
    editor: number;
    reporting: number;
    noRole: number;
    external: number;
    disabled: number;
    postedInPeriod: number;
  };
  posts: {
    period: number;
    average: number;
    total: number;
    /** Median hours between the observation and it being written up. */
    entryLagMedianHours: number | null;
    /** Distinct device logins that made anything in the period. */
    devices: number;
  };
  publishing: {
    period: number;
    average: number;
    backlog: number;
    backlogOldestDays: number | null;
    lagMedianHours: number | null;
  };
  photos: {
    period: number;
    average: number;
    /** Uploads showing a face-blurred variant. */
    blurred: number;
    total: number;
  };
  reports: {
    period: number;
    average: number;
    total: number;
    templates: { name: string; count: number }[];
  };
  ai: {
    cost: number;
    costAverage: number;
    enrichment: number;
    typoCorrection: number;
    embedding: number;
    calls: number;
    failures: number;
    currency: string;
  };
  features: {
    attendance: number;
    /** Documents added OR changed — the tables carry no created_at. */
    childDocuments: number;
    orgDocuments: number;
    emails: number;
  };
  support: {
    pinEvents: number;
    pinLockouts: number;
    emailBounces: number;
    emailComplaints: number;
  };
  lastActivityAt: string | null;
  daily: DailyActivity[];
  heatmap: HeatmapCell[];
  trend: TrendBucket[];
}

export interface DailyActivity {
  /** YYYY-MM-DD, UK local. Zero-filled to today, never into the future. */
  day: string;
  posts: number;
  photos: number;
  published: number;
  reports: number;
  attendance: number;
}

export interface HeatmapCell {
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  hour: number;
  posts: number;
}

/** One bucket of the series the averages are computed from. */
export interface TrendBucket {
  start: string;
  /** True for the period currently on screen. */
  isCurrent: boolean;
  posts: number;
  photos: number;
  published: number;
  reports: number;
  aiCost: number;
}

export class SystemInsightsDataService {
  /** Every organisation's headline numbers for one period. */
  static async fetchPlatform(period: InsightPeriod): Promise<PlatformInsights> {
    const response = await api.get('/system-admin/insights', { params: { period } });
    return response.data;
  }

  /** One organisation in full, including its series. */
  static async fetchOrg(orgId: number, period: InsightPeriod): Promise<OrgInsights> {
    const response = await api.get(`/system-admin/organisations/${orgId}/insights`, { params: { period } });
    return response.data;
  }
}

/** Whole days since an instant. null in, null out. */
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

/**
 * "Quiet" is the churn signal: nothing made for a fortnight. Two weeks rather
 * than one so a half-term or a holiday doesn't light the whole table up.
 */
export const QUIET_DAYS = 14;

/** "3 days ago" / "today" — a relative age is what's being asked, not a date. */
export function lastActivityLabel(iso: string | null): string {
  const days = daysSince(iso);
  if (days === null) return 'Never';
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 31) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months <= 1 ? 'Over a month ago' : `${months} months ago`;
}

/** Cost with the currency the events were priced in. Fractions of a cent matter. */
export function formatCost(value: number | null | undefined, currency = 'USD'): string {
  if (value == null) return '—';
  const decimals = value !== 0 && Math.abs(value) < 1 ? 4 : 2;
  const body = value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return currency === 'USD' ? `$${body}` : `${body} ${currency}`;
}

/** A count against its average, e.g. "vs 41.2 avg". */
export function averageLabel(average: number, unit: 'week' | 'month' | string | null): string {
  const per = unit === 'month' ? 'month' : 'week';
  return `vs ${average.toLocaleString(undefined, { maximumFractionDigits: 1 })} avg per ${per}`;
}

/** Hours as something readable: "4h", "2 days", "—". */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null) return '—';
  if (hours < 1) return 'Under an hour';
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)} days`;
}
